import PropTypes from 'prop-types';
import React, { useRef, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { useSelector } from 'react-redux';

import { userSelector } from '../../../../reducers/selectors';
import IconCross from '../../../Icons/IconCross';
import InputField from '../../../InputField';

import './index.scss';

const CosignerModal = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  content,
  confirmText,
  handleCosignerDetails,
}) => {
  const modalRef = useRef(null);
  const user = useSelector(userSelector);

  // State for input values and validation errors
  const [cosignerName, setCosignerName] = useState('');
  const [cosignerSurname, setCosignerSurname] = useState('');
  const [cosignerEmail, setCosignerEmail] = useState('');
  const [nameError, setNameError] = useState('');
  const [surnameError, setSurnameError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [wasValidated, setWasValidated] = useState(false);

  // Function to validate name and surname input
  const validateName = (value, variableName) => {
    const nameRegex = /^[a-zñáéíóúäëïöüçàèìòùßøåæþð ]{2,}(?:-[a-zñáéíóúäëïöüçàèìòùßøåæþð]{1,}| [a-zñáéíóúäëïöüçàèìòùßøåæþð]{2,})*$/i;
    if (!value.trim()) {
      return `${variableName} is required`;
    }

    if (!nameRegex.test(value)) {
      return `${variableName} can only contain alphabets and spaces.`;
    }

    return '';
  };

  // Function to validate email input
  const validateEmail = cosignerEmail => {
    const emailRegex = new RegExp(
      /^([a-zA-Z\d_\-^+$'&#\\/!%*=?`|~]|[^\x00-\x7F\s])(([a-zA-Z\d_\-^+$'&#\\/!%*=?`|~.]|[^\x00-\x7F\s])*([a-zA-Z\d_\-^+$'&#\\/!%*=?`|~]|[^\x00-\x7F\s]))?@[a-zA-Z\d](?:[a-zA-Z\d-]{0,61}[a-zA-Z\d])?(?:\.[a-zA-Z\d](?:[a-zA-Z\d-]{0,61}[a-zA-Z\d])?)+$/,
    );
    if (!cosignerEmail.trim()) {
      return 'Email is required';
    }

    if (!emailRegex.test(cosignerEmail)) {
      return 'Please enter a valid email address.';
    }

    if (user.profile.email.toLowerCase() === cosignerEmail.toLowerCase()) {
      return 'The cosigner’s email cannot be the same as your email.';
    }

    return '';
  };

  const handleNameChange = event => {
    const name = event.target.value;
    setCosignerName(name);
    handleCosignerDetails({ cosignerName: name });
    // Only update errors if form was previously validated
    if (wasValidated) {
      setNameError(validateName(name, 'Name'));
    }
  };

  const handleSurnameChange = event => {
    const surname = event.target.value;
    setCosignerSurname(surname);
    handleCosignerDetails({ cosignerSurname: surname });
    // Only update errors if form was previously validated
    if (wasValidated) {
      setSurnameError(validateName(surname, 'Surname'));
    }
  };

  const handleEmailChange = event => {
    const email = event.target.value;
    setCosignerEmail(email);
    handleCosignerDetails({ cosignerEmail: email });
    // Only update errors if form was previously validated
    if (wasValidated) {
      setEmailError(validateEmail(email));
    }
  };

  const handleConfirmClick = () => {
    setWasValidated(true);
    const newNameError = validateName(cosignerName, 'Name');
    const newSurnameError = validateName(cosignerSurname, 'Surname');
    const newEmailError = validateEmail(cosignerEmail);

    setNameError(newNameError);
    setSurnameError(newSurnameError);
    setEmailError(newEmailError);

    // Only proceed with onConfirm if there are no errors
    if (!newNameError && !newEmailError && !newSurnameError) {
      onConfirm();
    }
  };

  const handleClickOutside = event => {
    if (modalRef.current && !modalRef.current.contains(event.target)) {
      onClose();
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  });

  // Reset validation state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setWasValidated(false);
      setNameError('');
      setEmailError('');
    }
  }, [isOpen]);

  if (isOpen) {
    return ReactDOM.createPortal(
      <div className="cosigner-modal-overlay fade-in">
        <div
          className="cosigner-modal modal-fade-in"
          role="dialog"
          aria-labelledby="dialog-title"
          aria-modal="true"
          ref={modalRef}
        >
          <div className="cosigner-modal__title">
            <h4>{title}</h4>
            <button
              className="cosigner-modal__close"
              title="close"
              aria-label="Close modal"
              type="button"
              onClick={onClose}
            >
              <IconCross />
            </button>
          </div>

          <div className="cosigner-modal__content">
            {content}
            <div className="input-container">
              <InputField
                type="text"
                id="cosigner-name"
                label="Co-signer first name"
                value={cosignerName}
                onChange={handleNameChange}
                required
              />
              {nameError && <p className="error-message">{nameError}</p>}
              <InputField
                type="text"
                id="cosigner-surname"
                label="Co-signer surname"
                value={cosignerSurname}
                onChange={handleSurnameChange}
                required
              />
              {surnameError && <p className="error-message">{surnameError}</p>}
              <InputField
                type="text"
                id="cosigner-email"
                label="Co-signer email"
                value={cosignerEmail}
                onChange={handleEmailChange}
                required
              />
              {emailError && <p className="error-message">{emailError}</p>}
            </div>
          </div>

          <div className="cosigner-modal__footer">
            <button
              className="confirm"
              onClick={handleConfirmClick}
              disabled={wasValidated && (!!nameError || !!emailError || !!surnameError)}
            >
              {confirmText}
            </button>
          </div>
        </div>
      </div>,
      document.body,
    );
  }

  return null;
};

CosignerModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  content: PropTypes.string.isRequired,
  confirmText: PropTypes.string.isRequired,
  handleCosignerDetails: PropTypes.func.isRequired,
};

export default CosignerModal;
